import { useEffect, useMemo, useRef, useState } from "react";
import { findCuratedBeach, beachKey } from "../../shared/beaches";
import {
  addBeachToTrip,
  removeBeachFromTrip,
  rememberPlace,
  useFishStore,
} from "../lib/storage";
import {
  localPlaceMatches,
  geoPlaceMatches,
  placeKeys,
  type PlaceItem,
} from "../lib/placeSearch";
import { CloseIcon, PinIcon } from "../ui";
import { useModalChrome } from "../lib/modalUx";
import { SpotConfirm } from "./SpotConfirm";

// BeachPicker — modal that adds a named snorkel spot to a trip.
//
// Behavior:
//   • Unified place search (lib/placeSearch): known snorkel spots first,
//     then spots you've logged fish at, then general geocoded locations —
//     so any real place can be attached, with coordinates, ranked with the
//     water spots on top.
//   • Free-text fallback: if nothing matches at all, an "Add '<typed>'"
//     option still appears (attaches with no coordinates).
//   • De-dup: adding a name already on the trip is a no-op (silent).
//   • Trip-scoped: attaches to the active trip, not to any species.

export function BeachPicker({
  tripId,
  onClose,
}: {
  tripId: string;
  onClose: () => void;
}) {
  const [text, setText] = useState("");
  const [highlight, setHighlight] = useState(0);
  // A picked curated beach awaiting on-map confirmation before it's attached.
  const [pending, setPending] = useState<{
    name: string;
    lat: number;
    lng: number;
    region?: string;
  } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const trips = useFishStore((s) => s.trips);
  const visited = useFishStore((s) => s.visited_locations);
  const trip = trips.find((t) => t.id === tripId);
  const existing = useMemo(
    () =>
      new Set(
        (trip?.beaches ?? []).map((b) =>
          b.trim().replace(/\s+/g, " ").toLowerCase(),
        ),
      ),
    [trip],
  );

  // Instant local matches (curated + your logged spots), ranked water-first.
  const local = useMemo(
    () =>
      localPlaceMatches(text, visited).filter(
        (p) => !existing.has(p.name.trim().replace(/\s+/g, " ").toLowerCase()),
      ),
    [text, visited, existing],
  );

  // Debounced geocoded fallback — general map locations, de-duped against the
  // local matches and what's already on the trip.
  const [geo, setGeo] = useState<PlaceItem[]>([]);
  useEffect(() => {
    const q = text.trim();
    if (q.length < 2) {
      setGeo([]);
      return;
    }
    let cancelled = false;
    const ctl = new AbortController();
    const timer = setTimeout(() => {
      geoPlaceMatches(q, placeKeys(local), ctl.signal)
        .then((r) => {
          if (!cancelled) {
            setGeo(
              r.filter(
                (p) =>
                  !existing.has(
                    p.name.trim().replace(/\s+/g, " ").toLowerCase(),
                  ),
              ),
            );
          }
        })
        .catch(() => {
          /* network/abort — local matches still shown */
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      ctl.abort();
    };
    // `local` is derived from text+visited+existing; keying on text avoids
    // re-firing the network call when only the memo identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, existing]);

  const cleanTyped = text.trim().replace(/\s+/g, " ");
  const allMatches = [...local, ...geo];
  const typedIsNew =
    cleanTyped.length > 0 &&
    !allMatches.some((b) => beachKey(b.name) === beachKey(cleanTyped)) &&
    !existing.has(cleanTyped.toLowerCase());

  const rows: {
    name: string;
    detail?: string;
    isTyped?: boolean;
    source?: PlaceItem["source"];
    lat?: number;
    lng?: number;
  }[] = [
    ...allMatches.map((p) => ({
      name: p.name,
      detail: p.detail,
      source: p.source,
      lat: p.lat,
      lng: p.lng,
    })),
    ...(typedIsNew ? [{ name: cleanTyped, isTyped: true }] : []),
  ];

  useModalChrome(true, onClose, dialogRef);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  useEffect(() => {
    setHighlight(0);
  }, [text]);

  // Picking a spot with coordinates (a curated beach or one you've logged
  // fish at) shows it on a map for confirmation first; a free-typed spot has
  // no coords to plot, so it attaches straight away. See
  // beach-picker-confirm-on-map.
  const add = (
    name: string,
    coords?: { lat?: number; lng?: number; region?: string },
  ) => {
    let lat = coords?.lat;
    let lng = coords?.lng;
    let region = coords?.region;
    if (lat == null || lng == null) {
      const curated = findCuratedBeach(name);
      if (curated?.lat != null && curated?.lng != null) {
        lat = curated.lat;
        lng = curated.lng;
        region = curated.region;
      }
    }
    if (lat != null && lng != null) {
      setPending({ name, lat, lng, region });
      return;
    }
    commit(name);
  };

  const commit = (
    name: string,
    coords?: { lat: number; lng: number },
  ) => {
    // Option B: a non-curated place picked from search remembers its
    // coordinates (without clobbering a real logged visit) so it resolves on
    // the map, the Plan page, and the shared trip later.
    if (coords && !findCuratedBeach(name)) {
      rememberPlace(name, coords.lat, coords.lng);
    }
    addBeachToTrip(tripId, name);
    setText("");
    setPending(null);
    // Keep focus on the input so the user can add another without a click.
    inputRef.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const pick = rows[highlight];
      if (pick) add(pick.name, { lat: pick.lat, lng: pick.lng });
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, rows.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    }
  };

  if (!trip) return null;

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-ink/40 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="flex max-h-[85dvh] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl bg-paper pb-[env(safe-area-inset-bottom)] shadow-card focus:outline-none sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Add a beach"
      >
        <header className="flex items-center justify-between px-5 pt-4">
          <div className="min-w-0">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-meta">
              Beach for
            </p>
            <p className="truncate font-display text-lg font-semibold text-ink">
              {trip.name}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid h-11 w-11 place-items-center rounded-full text-meta hover:bg-ink/5"
          >
            <CloseIcon className="h-4 w-4" />
          </button>
        </header>

        {pending && (
          <div className="px-5 pb-4 pt-4">
            <SpotConfirm
              name={pending.name}
              region={pending.region}
              lat={pending.lat}
              lng={pending.lng}
              confirmLabel="Add to trip"
              onConfirm={() =>
                commit(pending.name, { lat: pending.lat, lng: pending.lng })
              }
              onBack={() => setPending(null)}
            />
          </div>
        )}

        <div className={`px-5 pt-4 ${pending ? "hidden" : ""}`}>
          <input
            ref={inputRef}
            className="field w-full"
            placeholder="Start typing a beach — Two Step, Molokini, Hanauma…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            autoComplete="off"
            enterKeyHint="done"
            aria-label="Beach name"
          />
        </div>

        <ul
          role="listbox"
          aria-label="Beach suggestions"
          className={`min-h-0 flex-1 overflow-y-auto px-3 pb-4 pt-3 ${
            pending ? "hidden" : ""
          }`}
        >
          {rows.length === 0 && (
            <li className="px-3 py-4 text-center text-sm text-meta">
              {existing.size > 0
                ? "Every beach in the list is already on this trip."
                : "Start typing to see suggestions."}
            </li>
          )}
          {rows.map((r, i) => {
            const on = i === highlight;
            return (
              <li key={`${r.name}-${i}`} role="option" aria-selected={on}>
                <button
                  type="button"
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => add(r.name, { lat: r.lat, lng: r.lng })}
                  className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition ${
                    on ? "bg-aqua/10" : "hover:bg-ink/[0.03]"
                  }`}
                >
                  <PinIcon
                    className={`h-4 w-4 flex-none ${
                      r.source === "geo" ? "text-meta" : "text-aqua-deep"
                    }`}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-display text-[15px] font-semibold text-ink">
                      {r.isTyped ? `Add "${r.name}"` : r.name}
                    </span>
                    {r.detail && (
                      <span className="block truncate text-[11.5px] text-meta">
                        {r.detail}
                      </span>
                    )}
                    {r.source === "curated" && (
                      <span className="block truncate text-[11.5px] text-aqua-deep">
                        Known snorkel spot
                      </span>
                    )}
                    {r.source === "logged" && (
                      <span className="block truncate text-[11.5px] text-aqua-deep">
                        A spot you've logged fish at
                      </span>
                    )}
                    {r.isTyped && (
                      <span className="block truncate text-[11.5px] text-meta">
                        Not in the curated list — add anyway
                      </span>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        {(trip.beaches ?? []).length > 0 && (
          <footer className="border-t border-hairline px-5 py-3">
            <p className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.14em] text-meta">
              Already on this trip · {trip.beaches?.length}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {(trip.beaches ?? []).map((b) => (
                <span
                  key={b}
                  className="inline-flex min-h-[44px] items-center gap-1 rounded-full bg-ink/[0.06] pl-3 pr-1 text-[13px] font-semibold text-ink"
                >
                  {b}
                  <button
                    type="button"
                    onClick={() => removeBeachFromTrip(tripId, b)}
                    aria-label={`Remove ${b}`}
                    className="ml-1 inline-flex h-11 w-11 items-center justify-center rounded-full text-ink/40 hover:bg-coral/10 hover:text-coral"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </footer>
        )}
      </div>
    </div>
  );
}
